import type { ComponentType } from 'react'
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { SiteLayout } from '../components/SiteLayout'
import { SectionCTA } from '../components/sections/SectionCTA'
import { customerStories, type ContentEntry } from '../lib/content'
import { CustomerCard } from '../components/CustomerCard'

// Per-customer accent, sampled from each brand's testimonial card and tuned for
// legibility on paper. Each story page picks up its own colour while the layout
// stays shared; unknown companies fall back to the brand violet.
const ACCENTS: Record<string, string> = {
  Altura: '#7c3aed',
  Freeday: '#4f46e5',
  Hyperfox: '#a3309e',
  Vinny: '#a85f37',
  Roojoom: '#6d4fd6',
  HolidayHero: '#138b84',
  'Struck.build': '#2f6fd0',
}
const DEFAULT_ACCENT = '#d65209'

/**
 * CustomerStoryPage: the dedicated template behind every /customers/<slug>.
 *
 * The brief: a reader should come away knowing what this team does and how
 * they use LangWatch. So the narrative (the MDX body) is the spine of the
 * page; everything else frames it. A sticky spec rail on the left states the
 * facts (company, industry, use case, tools, results) like a case file; the
 * story reads down the right.
 *
 * Every structured block is optional. A new story needs only title + intro +
 * body to render cleanly; fill in the frontmatter fields (company, industry,
 * useCase, tools, results, quote...) and the rail and pull quote light up.
 * That is the "template already running" part: adding a customer is dropping
 * in one .mdx, no page work.
 */
export function CustomerStoryPage({
  entry,
  Content,
}: {
  entry: ContentEntry
  Content?: ComponentType
}) {
  const { meta } = entry
  const accent = ACCENTS[meta.company ?? ''] ?? DEFAULT_ACCENT
  const heroCard = meta.card ?? meta.image
  const related = customerStories()
    .filter((s) => s.slug !== entry.slug)
    .slice(0, 3)

  return (
    <SiteLayout>
      <article className="relative isolate pt-28 pb-20 md:pt-32 md:pb-28">
        {/* Ambient per-customer accent wash at the top of the page. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[480px]"
          style={{ background: `radial-gradient(75% 60% at 18% 0%, ${accent}14, transparent 70%)` }}
        />
        {/* ── Hero ─────────────────────────────────────────────── */}
        <header className="container-wide">
          <a
            href="/customers"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Customer stories
          </a>

          <div className="mt-8 grid items-center gap-10 md:mt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)] lg:gap-14">
            <div>
              <div className="mb-5 h-1 w-12 rounded-full" style={{ backgroundColor: accent }} />
              <div className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-400">
                <span style={{ color: accent }}>Customer story</span>
                {meta.industry && (
                  <>
                    <span className="h-1 w-1 rounded-full bg-ink-300" />
                    <span>{meta.industry}</span>
                  </>
                )}
              </div>
              <h1 className="display mt-4 text-[32px] leading-[1.04] text-ink-900 md:text-[52px]">
                {meta.title}
              </h1>
              {meta.intro && (
                <p className="mt-5 text-[18px] leading-[1.6] text-ink-600 md:text-[20px]">
                  {meta.intro}
                </p>
              )}
            </div>

            {/* The customer's own testimonial card, shown whole (never cropped). */}
            {heroCard && (
              <div className="lg:justify-self-end">
                <img
                  src={heroCard}
                  alt={`${meta.company ?? meta.title} on LangWatch`}
                  className="w-full max-w-[440px] rounded-2xl border border-ink-100 shadow-[0_30px_80px_-32px_rgba(20,20,45,0.4)]"
                  loading="eager"
                />
              </div>
            )}
          </div>
        </header>

        {/* ── Spec rail + narrative ────────────────────────────── */}
        <div className="container-wide mt-12 md:mt-16">
          <div className="grid grid-cols-1 gap-y-12 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-x-16 xl:gap-x-24">
            {/* Left rail: the facts, stated plainly. Sticky on desktop so it
                stays with the reader through a long story. */}
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <SpecRail meta={meta} accent={accent} />
              {meta.results && meta.results.length > 0 && (
                <ResultsRail results={meta.results} accent={accent} />
              )}
            </aside>

            {/* Right: the pull quote, then the story. */}
            <div className="min-w-0">
              {meta.quote && (
                <PullQuote
                  quote={meta.quote}
                  author={meta.quoteAuthor}
                  role={meta.quoteRole}
                  accent={accent}
                />
              )}
              <div className="prose max-w-[680px]">
                {Content ? <Content /> : null}
              </div>
            </div>
          </div>
        </div>

        {/* ── More stories ─────────────────────────────────────── */}
        {related.length > 0 && (
          <section className="container-wide mt-24 border-t border-ink-100 pt-14 md:mt-28">
            <div className="flex items-end justify-between gap-4">
              <h2 className="display text-[26px] text-ink-900 md:text-[34px]">
                More customer stories
              </h2>
              <a
                href="/customers"
                className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-ink-600 transition-colors hover:text-ink-900"
              >
                See all
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-x-7 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((s) => (
                <CustomerCard key={s.slug} entry={s} titleAs="h3" />
              ))}
            </div>
          </section>
        )}
      </article>

      <SectionCTA />
    </SiteLayout>
  )
}

/** Mono-labelled definition list: the case-file facts about the customer. */
function SpecRail({ meta, accent }: { meta: ContentEntry['meta']; accent: string }) {
  const rows: { label: string; value: string }[] = []
  if (meta.company) rows.push({ label: 'Company', value: meta.company })
  if (meta.industry) rows.push({ label: 'Industry', value: meta.industry })
  if (meta.useCase) rows.push({ label: 'Use case', value: meta.useCase })

  return (
    <dl className="border-t-2" style={{ borderColor: accent }}>
      {rows.map((r) => (
        <div
          key={r.label}
          className="flex flex-col gap-1 border-b border-ink-100 py-3.5"
        >
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
            {r.label}
          </dt>
          <dd className="text-[14px] font-medium leading-[1.4] text-ink-900">
            {r.value}
          </dd>
        </div>
      ))}

      {meta.tools && meta.tools.length > 0 && (
        <div className="flex flex-col gap-2 border-b border-ink-100 py-3.5">
          <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
            LangWatch in use
          </dt>
          <dd className="flex flex-wrap gap-1.5">
            {meta.tools.map((tool) => (
              <span
                key={tool}
                className="rounded-full border border-ink-200 bg-paper px-2.5 py-0.5 text-[11.5px] font-medium text-ink-700"
              >
                {tool}
              </span>
            ))}
          </dd>
        </div>
      )}

      {meta.website && (
        <a
          href={meta.website}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: accent }}
          className="group mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium transition-opacity hover:opacity-80"
        >
          Visit {meta.company ?? 'website'}
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      )}
    </dl>
  )
}

/** The headline outcomes, stacked under the spec rail. The number carries the
 * blue-to-orange brand gradient; the label says what it measures. */
function ResultsRail({ results, accent }: { results: NonNullable<ContentEntry['meta']['results']>; accent: string }) {
  return (
    <div className="mt-9">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
        Results
      </div>
      <div className="mt-3 flex flex-col gap-5">
        {results.map((r, i) => (
          <div key={i}>
            <div className="display text-[30px] leading-[1] md:text-[34px]" style={{ color: accent }}>
              {r.stat}
            </div>
            <div className="mt-1.5 text-[13px] leading-[1.45] text-ink-500">
              {r.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Lead-in pull quote: the customer's words, set large, with a gradient rule. */
function PullQuote({
  quote,
  author,
  role,
  accent,
}: {
  quote: string
  author?: string
  role?: string
  accent: string
}) {
  return (
    <figure className="mb-12 border-l-2 pl-6 md:mb-14" style={{ borderColor: accent }}>
      <blockquote className="display text-[24px] leading-[1.3] text-ink-900 md:text-[30px]">
        {quote}
      </blockquote>
      {(author || role) && (
        <figcaption className="mt-4 text-[14px] text-ink-500">
          {author && <span className="font-semibold text-ink-800">{author}</span>}
          {author && role && <span className="px-2 text-ink-300">/</span>}
          {role && <span>{role}</span>}
        </figcaption>
      )}
    </figure>
  )
}
